import {
  IInvoiceRespSchema,
  PaymentGatewayConfigurations,
  PaymentMethodSummaryResponse,
  SharedInvoicePaymentExistingMethod,
} from '@monetize/types/app';
import { formatCurrency, getCurrencyDecimals } from '@monetize/utils/core';
import { FunctionComponent as FC, useEffect, useMemo } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import {
  MCustomNumberInput,
  MFormField,
  MGrid,
  MGridItem,
  MText,
} from '../../../components/Monetize';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import { PaymentMethodItemRenderer } from '../../../components/PaymentMethods/PaymentMethodItemRenderer';
import {
  getGroupedAndSortedPaymentMethodOptions,
  getPaymentAmountLimits,
  validatePaymentAmountLimits,
} from '../../../utils/payment.utils';

interface SharedInvoiceFormExistingPaymentMethodProps {
  allowPartialPayment: boolean;
  formState: UseFormReturn<SharedInvoicePaymentExistingMethod>;
  invoice: IInvoiceRespSchema;
  isDisabled?: boolean;
  paymentMethods: PaymentMethodSummaryResponse[];
  paymentConfiguration?: PaymentGatewayConfigurations;
}

export const SharedInvoiceFormExistingPaymentMethod: FC<
  SharedInvoiceFormExistingPaymentMethodProps
> = ({
  allowPartialPayment,
  formState,
  invoice,
  isDisabled,
  paymentMethods,
  paymentConfiguration,
}: SharedInvoiceFormExistingPaymentMethodProps) => {
  const {
    control,
    watch,
    clearErrors,
    formState: { errors },
  } = formState;

  const paymentMethodItems = useMemo(
    () => getGroupedAndSortedPaymentMethodOptions(paymentMethods),
    [paymentMethods],
  );

  const selectedPaymentMethodId = watch('paymentMethodId');
  const amount = watch('amount');
  const { currency } = invoice;

  // Clear any submit-time RHF error when payment method changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => clearErrors('amount'), [selectedPaymentMethodId]);

  const selectedPaymentMethod = useMemo(
    () => paymentMethods.find(({ id }) => id === selectedPaymentMethodId),
    [paymentMethods, selectedPaymentMethodId],
  );

  const amountLimits = useMemo(() => {
    if (!selectedPaymentMethod) {
      return null;
    }
    return getPaymentAmountLimits(
      paymentConfiguration,
      selectedPaymentMethod.paymentMethodType,
      currency,
    );
  }, [paymentConfiguration, selectedPaymentMethod, currency]);

  // Derive amount limit error directly — no useEffect/setError needed
  const amountLimitError = useMemo(() => {
    if (!amountLimits) {
      return null;
    }
    return validatePaymentAmountLimits(amountLimits, amount, currency);
  }, [amountLimits, amount, currency]);

  return (
    <MGrid templateColumns="repeat(12, 1fr)" gap={4} my={3}>
      <MGridItem colSpan={12}>
        <MFormField
          isDisabled={isDisabled}
          error={
            errors?.amount ||
            (amountLimitError
              ? { message: amountLimitError, type: 'manual' }
              : undefined)
          }
          label="Amount"
          isRequired
        >
          <Controller
            name="amount"
            control={control}
            render={({ field: { onChange, ...field } }) =>
              allowPartialPayment ? (
                <MCustomNumberInput
                  precision={getCurrencyDecimals(currency)}
                  isDisabled={isDisabled}
                  onChange={(_, valueAsNumber) => onChange(valueAsNumber)}
                  {...field}
                />
              ) : (
                <MText>{formatCurrency(field.value, { currency })}</MText>
              )
            }
          />
        </MFormField>
      </MGridItem>
      <MGridItem colSpan={12}>
        <MFormField
          isDisabled={isDisabled}
          error={errors?.paymentMethodId}
          label="Payment Method"
          isRequired
        >
          <Controller
            name="paymentMethodId"
            control={control}
            render={({ field }) => (
              <MCustomSelect
                items={paymentMethodItems}
                itemTitle="name"
                itemValue="id"
                renderItemContent={PaymentMethodItemRenderer}
                {...field}
              />
            )}
          />
        </MFormField>
      </MGridItem>
    </MGrid>
  );
};
